import {Action, State, StateContext, Store} from '@ngxs/store';
import {AdvertiserFavoriteDevicesModel} from './advertiser-favorite-devices.model';
import {Injectable} from '@angular/core';
import {AdvertiserDevicesService} from '../../services-advertiser/devices/devices.service';
import {
  CleanAdvertiserFavoriteDeviceStore,
  FetchAdvertiserFavoriteDevices,
  GetAdvertiserFavoriteDevicesBySubmit,
  RemoveAdvertiserFavoriteDeviceFromFavorite,
  SearchAdvertiserFavoriteDevices,
  SetAdvertiserFavoriteDevicesPage,
  SetAdvertiserMapBoundsForFavorite,
  SortAdvertiserFavoriteDevices
} from './advertiser-favorite-devices.actions';
import {Observable} from 'rxjs';
import {IDeviceAdvertiser} from '@interfaces/advertiser/devices';
import {IResponse} from '@interfaces/response';
import {switchMap, tap} from 'rxjs/operators';
import {removeEmptyPropsFromObj} from '@shared/functions';
import {RouterSelectors} from '@store/router/router.selectors';

@State<AdvertiserFavoriteDevicesModel>({
  name: 'advertiserFavoriteDevices',
  defaults: {
    mapBounds: null,
    searchParam: '',
    devices: null,
    orientation: null,
    priceFrom: null,
    priceTo: null,
    deviceConfigList: {sort: 'id', order: 'DESC', itemsPerPage: 10, loader: false, currentPage: 1}
  }
})

@Injectable()
export class AdvertiserFavoriteDevicesState {
  constructor(private deviceService: AdvertiserDevicesService,
              private store: Store) {
  }

  @Action(FetchAdvertiserFavoriteDevices, {cancelUncompleted: true})
  fetchAdvertiserFavoriteDevices(ctx: StateContext<AdvertiserFavoriteDevicesModel>): Observable<IResponse<IDeviceAdvertiser>> {
    const limit = ctx.getState().deviceConfigList.itemsPerPage;
    const skip = limit * (ctx.getState().deviceConfigList.currentPage - 1);
    const order = ctx.getState().deviceConfigList.order;
    const sort = ctx.getState().deviceConfigList.sort;
    const search = ctx.getState().searchParam;
    const bounds = ctx.getState().mapBounds;

    const priceFrom = ctx.getState().priceFrom;
    const priceTo = ctx.getState().priceTo;
    const orientationType = ctx.getState().orientation;

    const filteredProps = removeEmptyPropsFromObj({priceFrom, priceTo, orientationType});

    ctx.patchState({deviceConfigList: {...ctx.getState().deviceConfigList, loader: true}});
    const mapRequest = this.deviceService.getAdvertiserFavoriteDevices({
      lngMax: bounds?.getEast(), lngMin: bounds?.getWest(),
      latMin: bounds?.getSouth(), latMax: bounds?.getNorth(), search, ...filteredProps
    }).pipe(
      tap((devices) => {
        const state = ctx.getState();
        ctx.setState({...state, devices, deviceConfigList: {...ctx.getState().deviceConfigList, loader: false}});
      })
    );

    const listRequest = this.deviceService.getAdvertiserFavoriteDevices({
      search, ...filteredProps,
      limit, order, sort, skip
    }).pipe(
      tap((devices) => {
        const state = ctx.getState();
        ctx.setState({...state, devices, deviceConfigList: {...ctx.getState().deviceConfigList, loader: false}});
      })
    );

    const isListPage = this.store.selectSnapshot(RouterSelectors.data).localizeRouter.path === 'list';

    if (isListPage) return listRequest;
    return mapRequest;
  }

  @Action(SetAdvertiserMapBoundsForFavorite)
  setAdvertiserMapBounds(ctx: StateContext<AdvertiserFavoriteDevicesModel>, {mapBounds}: SetAdvertiserMapBoundsForFavorite): Observable<any> {
    ctx.patchState({mapBounds});
    return ctx.dispatch(new FetchAdvertiserFavoriteDevices());
  }

  @Action(SetAdvertiserFavoriteDevicesPage)
  setAdvertiserFavoriteDevicesPage(ctx: StateContext<AdvertiserFavoriteDevicesModel>, {page}: SetAdvertiserFavoriteDevicesPage): Observable<any> {
    ctx.patchState({deviceConfigList: {...ctx.getState().deviceConfigList, currentPage: page}});
    return ctx.dispatch(new FetchAdvertiserFavoriteDevices());
  }

  @Action(SearchAdvertiserFavoriteDevices)
  searchAdvertiserFavoriteDevicesPage(ctx: StateContext<AdvertiserFavoriteDevicesModel>, {search}: SearchAdvertiserFavoriteDevices): Observable<any> {
    ctx.patchState({deviceConfigList: {...ctx.getState().deviceConfigList, currentPage: 1}, searchParam: search});
    return ctx.dispatch(new FetchAdvertiserFavoriteDevices());
  }

  @Action(SortAdvertiserFavoriteDevices)
  sortAdvertiserFavoriteDevices(ctx: StateContext<AdvertiserFavoriteDevicesModel>, {payload}: SortAdvertiserFavoriteDevices): Observable<any> {
    ctx.patchState({deviceConfigList: {...ctx.getState().deviceConfigList, sort: payload.sort, order: payload.order}});
    return ctx.dispatch(new FetchAdvertiserFavoriteDevices());
  }

  @Action(GetAdvertiserFavoriteDevicesBySubmit)
  getAdvertiserDevicesBySubmit(ctx: StateContext<AdvertiserFavoriteDevicesModel>, {payload}: GetAdvertiserFavoriteDevicesBySubmit): void {
    ctx.setState({
      ...ctx.getState(), priceTo: payload?.priceTo || null,
      priceFrom: payload?.priceFrom || null, orientation: payload?.orientation || null
    });
  }

  @Action(RemoveAdvertiserFavoriteDeviceFromFavorite)
  removeAdvertiserFavoriteDeviceFromFavorite(ctx: StateContext<AdvertiserFavoriteDevicesModel>,
                                             {id}: RemoveAdvertiserFavoriteDeviceFromFavorite): Observable<any> {
    return this.deviceService.removeFromFavorite(id)
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const devices = state.devices?.rows || [];
          const newArr = devices.filter((device) => device.id !== id);
          const currentPage = this.getCurrentPageAfterDelete(
            state.deviceConfigList.currentPage,
            state.deviceConfigList.itemsPerPage,
            state.devices?.count || state.devices?.total,
            1
          );

          ctx.setState({
            ...state,
            deviceConfigList: {...state.deviceConfigList, currentPage},
            devices: {
              count: Math.max((state.devices?.count || 0) - 1, 0),
              rows: newArr,
              total: Math.max((state.devices?.total || 0) - 1, 0)
            }
          });
        }),
        switchMap(() => ctx.dispatch(new FetchAdvertiserFavoriteDevices()))
      );
  }

  @Action(CleanAdvertiserFavoriteDeviceStore)
  cleanAdvertiserFavoriteDeviceStore(ctx: StateContext<AdvertiserFavoriteDevicesModel>): void {
    ctx.setState({
      mapBounds: null,
      searchParam: '',
      devices: null,
      orientation: null,
      priceFrom: null,
      priceTo: null,
      deviceConfigList: {
        sort: 'id', order: 'DESC', itemsPerPage: ctx.getState().deviceConfigList.itemsPerPage, loader: false, currentPage: 1
      }
    });
  }

  private getCurrentPageAfterDelete(currentPage: number, perPage: number, total = 0, deletedCount = 1): number {
    const totalAfterDelete = Math.max(total - deletedCount, 0);
    const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / perPage), 1);

    return Math.min(currentPage, lastAvailablePage);
  }
}
